import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import {
  getAllCallLogs,
  CallLog,
  makeCallToLead,
  getRecordingByCallId,
} from "../../../api/calllog";
import "react-toastify/dist/ReactToastify.css";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import { Modal } from "react-bootstrap";
import moment from "moment";
import { highlightMatch } from "../../../core/common/highlightmatch";
import CollapseHeader from "../../../core/common/collapse-header";
import { Avatar, Tag } from "antd";

type Filters = {
  leadId: string;
  userId: string;
  status: string[];
  startDate: Date | null;
  endDate: Date | null;
  username: string;
};

const CallSettings: React.FC = () => {
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCalls, setSelectedCalls] = useState<string[]>([]);
  const [filters, setFilters] = useState<Filters>({
    leadId: "",
    userId: "",
    status: [],
    startDate: null,
    endDate: null,
    username: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>(null);
  const [activeStatusTab, setActiveStatusTab] = useState<string | null>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState<"pdf" | "excel">("pdf");
  const [selectedExportColumns, setSelectedExportColumns] = useState<string[]>([
    "start_time",
    "customer_number",
    "agent_number",
    "knowlarity_number",
    "call_duration",
    "call_recording",
  ]);
  const [isRecordingModalOpen, setIsRecordingModalOpen] = useState(false);
  const [activeRecording, setActiveRecording] = useState<CallLog | null>(null);

  // Date range picker settings
  const initialSettings = {
    startDate: moment().subtract(7, "days").toDate(),
    endDate: moment().toDate(),
    ranges: {
      Today: [moment().toDate(), moment().toDate()],
      Yesterday: [
        moment().subtract(1, "days").toDate(),
        moment().subtract(1, "days").toDate(),
      ],
      "Last 7 Days": [moment().subtract(6, "days").toDate(), moment().toDate()],
      "Last 30 Days": [
        moment().subtract(29, "days").toDate(),
        moment().toDate(),
      ],
      "This Month": [
        moment().startOf("month").toDate(),
        moment().endOf("month").toDate(),
      ],
      "Last Month": [
        moment().subtract(1, "month").startOf("month").toDate(),
        moment().subtract(1, "month").endOf("month").toDate(),
      ],
    },
    autoUpdateInput: true,
  };

  // Fetch call logs
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params = {
          lead_id: filters.leadId || undefined,
          username: filters.username || undefined,
          start_date: filters.startDate
            ? moment(filters.startDate).format("YYYY-MM-DD")
            : undefined,
          end_date: filters.endDate
            ? moment(filters.endDate).format("YYYY-MM-DD")
            : undefined,
        };
        const response = await getAllCallLogs(params);
        setCallLogs(response.data || []);
      } catch (error) {
        console.error("Error fetching call logs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filters]);

  // Handle call initiation
  const handleCallNow = async (customer_number: string) => {
    try {
      await makeCallToLead(null, customer_number);
      toast.success("Call successfully initiated "); // ✅ success toast
    } catch (error) {
      console.error("Error initiating call:", error);
      toast.error("Failed to initiate call ");
    }
  };

  // Fetch recording for a specific call
  const openRecordingModal = (record: CallLog) => {
    setActiveRecording(record);
    setIsRecordingModalOpen(true);
  };

  const closeRecordingModal = () => {
    setActiveRecording(null);
    setIsRecordingModalOpen(false);
  };

  // Calculate summary statistics
  const callStatusCounts = useMemo(() => {
    const counts: Record<string, number> = {
      Answered: 0,
      Missed: 0,
      Failed: 0,
    };
    callLogs.forEach((call) => {
      const status = call.agent_status || call.customer_status || "Unknown";
      if (status.includes("Missed")) {
        counts.Missed++;
      } else if (status.includes("Answered") || call.call_duration > 0) {
        counts.Answered++;
      } else {
        counts.Failed++;
      }
    });
    return counts;
  }, [callLogs]);

  // Process call logs for filtering and sorting
  const processedCalls = useMemo(() => {
    let filtered = [...callLogs];

    // Status filter
    if (filters.status.length > 0 || activeStatusTab !== null) {
      filtered = filtered.filter((call) => {
        const status = call.agent_status || call.customer_status || "Unknown";
        const effectiveStatus = status.includes("Missed")
          ? "Missed"
          : status.includes("Answered") || call.call_duration > 0
          ? "Answered"
          : "Failed";
        const matchesFilter =
          filters.status.length === 0 ||
          filters.status.includes(effectiveStatus);
        const matchesTab =
          !activeStatusTab || effectiveStatus === activeStatusTab;
        return matchesFilter && matchesTab;
      });
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((call) =>
        [
          call.customer_number,
          call.agent_number,
          call.knowlarity_number,
          call.call_recording,
          call.company_name,
          call.uuid,
        ].some((field) => field?.toLowerCase().includes(term))
      );
    }

    // Sorting
    if (!sortKey || !sortOrder) return filtered;

    return [...filtered].sort((a, b) => {
      const aValue = (a as any)[sortKey] || "";
      const bValue = (b as any)[sortKey] || "";
      if (aValue === bValue) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      return sortOrder === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [
    callLogs,
    filters.status,
    activeStatusTab,
    searchTerm,
    sortKey,
    sortOrder,
  ]);

  // Table columns
  const defaultColumnConfig = [
    { key: "SI_no", label: "S.No", visible: true },
    { key: "start_time", label: "Start Time", visible: true },
    { key: "customer_number", label: "Customer Number", visible: true },
    { key: "solution", label: "Solutions", visible: true },
    { key: "agent_number", label: "Agent Number", visible: true },
    { key: "destination", label: "Destination", visible: true },
    { key: "call_status", label: "Call Status", visible: true },
    { key: "agent_status", label: "Agent Status", visible: false },
    { key: "customer_status", label: "Customer Status", visible: false },
    { key: "knowlarity_number", label: "Knowlarity Number", visible: true },
    { key: "business_call_type", label: "Call Type", visible: true },
    { key: "call_duration", label: "Total Call Duration (s)", visible: true },
    { key: "call_recording", label: "Recording", visible: true },
    { key: "company_name", label: "Company Name", visible: true },
    { key: "uuid", label: "UUID", visible: false },
    { key: "order_id", label: "Order ID", visible: false },
    { key: "Call_Type", label: "Call Type Code", visible: false },
    { key: "credits_deducted", label: "Credits Deducted", visible: false },
    { key: "extension", label: "Extension", visible: false },
    { key: "caller_name", label: "Caller Name", visible: false },
    { key: "company_phone", label: "Company Phone", visible: false },
    { key: "agent_name", label: "Agent Name", visible: false },
    { key: "actions", label: "Actions", visible: true },
  ];

  const [columnConfig, setColumnConfig] = useState(defaultColumnConfig);

  const handleColumnToggle = (key: string) => {
    setColumnConfig((prev) =>
      prev.map((col) =>
        col.key === key ? { ...col, visible: !col.visible } : col
      )
    );
  };
  const statusColorMap: Record<string, string> = {
    answered: "green",
    missed: "red",
    busy: "volcano",
    ringing: "gold",
    failed: "geekblue",
  };
  const columns = useMemo(() => {
    const allColumnsMap: Record<string, any> = {
      SI_no: {
        title: "S.No",
        key: "SI_no",
        dataIndex: "SI_no",
        render: (_text: any, _record: any, index: number) =>
          (currentPage - 1) * pageSize + index + 1,
      },
      start_time: {
        title: "Date",
        dataIndex: "start_time",
        key: "start_time",
        render: (text: string) =>
          text ? (
            <div>
              <div>{new Date(text).toLocaleDateString()}</div>
              <div style={{ fontSize: "12px", color: "#888" }}>
                {new Date(text).toLocaleTimeString()}
              </div>
            </div>
          ) : (
            "-"
          ),
      },
      customer_number: {
        title: "Customer",
        dataIndex: "customer_number",
        render: (text: string, record: CallLog) => (
          <div className="d-flex align-items-center gap-2">
            <Avatar
              shape="square"
              style={{
                backgroundColor: "#ff4d4f",
                border: "2px solid #ff4d4f",
                width: 36,
                height: 36,
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              {text?.charAt(0).toUpperCase() || "U"}
            </Avatar>
            <div className="d-flex flex-column">
              <strong className="fw-semibold text-dark">
                {highlightMatch(record.company_name || "Unknown", searchTerm)}
              </strong>
              <span className="text-muted">
                {highlightMatch(text || "-", searchTerm)} (IN)
              </span>
            </div>
          </div>
        ),
      },
      agent_number: {
        title: "Agent",
        dataIndex: "agent_number",
        render: (text: string, record: CallLog) => (
          <div className="d-flex flex-column">
            <strong className="fw-semibold text-dark">
              {highlightMatch(record.agent_name || "Unknown", searchTerm)}
            </strong>
            <span className="text-muted">
              {highlightMatch(text || "-", searchTerm)} (IN)
            </span>
          </div>
        ),
      },
      destination: {
        title: "Destination",
        dataIndex: "destination",
        render: (text: string) => highlightMatch(text || "-", searchTerm),
      },
      call_status: {
        title: "Call Status",
        dataIndex: "call_status",
        render: (text: string) => {
          const status = text?.toLowerCase() || "-";

          const statusMap: Record<string, { label: string; color: string }> = {
            answered: { label: "Answered", color: "green" },
            missed: { label: "Missed", color: "red" },
            busy: { label: "Busy", color: "volcano" },
            failed: { label: "Failed", color: "geekblue" },
            ringing: { label: "Ringing", color: "gold" },
          };

          const { label, color } = statusMap[status] || {
            label: status,
            color: "default",
          };

          return <Tag color={color}>{highlightMatch(label, searchTerm)}</Tag>;
        },
      },
      knowlarity_number: {
        title: "Knowlarity Number",
        dataIndex: "knowlarity_number",
        render: (text: string) => highlightMatch(text || "-", searchTerm),
      },
      business_call_type: {
        title: "Call Type",
        dataIndex: "business_call_type",
        render: (text: string) => highlightMatch(text || "-", searchTerm),
      },
      call_duration: {
        title: "Total Call Duration",
        dataIndex: "call_duration",
        render: (duration: number) => {
          if (!duration) return "-";
          const minutes = Math.floor(duration / 60);
          const seconds = duration % 60;
          return `${minutes}m ${seconds}s`;
        },
      },
      call_recording: {
        title: "Call Recording",
        dataIndex: "recording_url",
        render: (_text: string, record: CallLog) => {
          const url = record.call_recording || record.call_recording;

          return url ? (
            <button
              className="rounded-circle border-0 bg-light d-flex align-items-center justify-content-center"
              style={{
                width: "50px",
                height: "50px",
                cursor: "pointer",
              }}
              onClick={() => openRecordingModal(record)}
            >
              <i className="bi bi-play-fill" />
            </button>
          ) : (
            "-"
          );
        },
      },

      company_name: {
        title: "Company Name",
        dataIndex: "company_name",
        render: (text: string) => highlightMatch(text || "Unknown", searchTerm),
      },
      uuid: {
        title: "UUID",
        dataIndex: "uuid",
        render: (text: string) => highlightMatch(text || "-", searchTerm),
      },
      order_id: {
        title: "Order ID",
        dataIndex: "order_id",
        render: (text: string) => highlightMatch(text || "-", searchTerm),
      },
      Call_Type: {
        title: "Call Type Code",
        dataIndex: "Call_Type",
        render: (text: string) => highlightMatch(text || "-", searchTerm),
      },
      credits_deducted: {
        title: "Credits Deducted",
        dataIndex: "credits_deducted",
        render: (text: string) => highlightMatch(text || "-", searchTerm),
      },
      extension: {
        title: "Extension",
        dataIndex: "extension",
        render: (text: string) => highlightMatch(text || "-", searchTerm),
      },
      caller_name: {
        title: "Caller Name",
        dataIndex: "caller_name",
        render: (text: string) => highlightMatch(text || "-", searchTerm),
      },
      timezone_offset: {
        title: "Timezone Offset",
        dataIndex: "timezone_offset",
        render: (text: string) => highlightMatch(text || "-", searchTerm),
      },
      company_phone: {
        title: "Company Phone",
        dataIndex: "company_phone",
        render: (text: string) => highlightMatch(text || "-", searchTerm),
      },
      agent_name: {
        title: "Agent Name",
        dataIndex: "agent_name",
        render: (text: string) => highlightMatch(text || "-", searchTerm),
      },
      agent_status: {
        title: "Agent Status",
        dataIndex: "agent_status",
        render: (text: string) => {
          const status = text?.toLowerCase() || "-";

          const statusMap: Record<string, { label: string; color: string }> = {
            connected: { label: "Connected", color: "green" },
            "no answer": { label: "No Answer", color: "volcano" },
            missed: { label: "Missed", color: "red" },
          };

          const { label, color } = statusMap[status] || {
            label: status,
            color: "default",
          };

          return <Tag color={color}>{highlightMatch(label, searchTerm)}</Tag>;
        },
      },
      customer_status: {
        title: "Customer Status",
        dataIndex: "customer_status",
        render: (text: string) => {
          const status = text?.toLowerCase() || "-";

          const statusMap: Record<string, { label: string; color: string }> = {
            connected: { label: "Connected", color: "green" },
            "no answer": { label: "No Answer", color: "volcano" },
            missed: { label: "Missed", color: "red" },
          };

          const { label, color } = statusMap[status] || {
            label: status,
            color: "default",
          };

          return <Tag color={color}>{highlightMatch(label, searchTerm)}</Tag>;
        },
      },
      solutions: {
        title: "Solutions",
        dataIndex: "solution",
        render: (text: string) => highlightMatch(text || "-", searchTerm),
      },
      actions: {
        title: "Actions",
        dataIndex: "actions",
        render: (_text: any, record: CallLog) => (
          <div className="dropdown table-action">
            <Link to="#" className="action-icon" data-bs-toggle="dropdown">
              <i className="fa fa-ellipsis-v"></i>
            </Link>
            <div className="dropdown-menu dropdown-menu-right">
              <Link
                className="dropdown-item"
                to="#"
                onClick={() => handleCallNow(record.customer_number)}
              >
                <i className="ti ti-phone text-blue"></i> Call Now
              </Link>
              <Link className="dropdown-item" to="#">
                <i className="ti ti-user-plus text-blue"></i> Add Contact
              </Link>
              <Link className="dropdown-item" to="#">
                <i className="ti ti-message text-blue"></i> SMS Now
              </Link>
              <Link className="dropdown-item" to="#">
                <i className="ti ti-tag text-blue"></i> Add Lead
              </Link>
              <Link className="dropdown-item" to="#">
                <i className="ti ti-ticket text-blue"></i> Add Ticket
              </Link>
            </div>
          </div>
        ),
      },
    };

    return columnConfig
      .filter((col) => col.visible)
      .map((col) => ({
        key: col.key,
        ...allColumnsMap[col.key],
      }));
  }, [columnConfig, searchTerm, currentPage, pageSize]);

  // Export column configuration
  const exportColumnConfig = [
    { key: "start_time", label: "Date" },
    { key: "customer_number", label: "Customer Number" },
    { key: "agent_number", label: "Agent Number" },
    { key: "knowlarity_number", label: "Knowlarity Number" },
    { key: "call_duration", label: "Duration" },
    { key: "call_recording", label: "Recording URL" },
    { key: "company_name", label: "Company Name" },
  ];

  // Handle export (placeholder, as API doesn't provide export functions)
  const handleExportAllCalls = async (type: "pdf" | "excel") => {
    toast.info(
      `Exporting all calls as ${type.toUpperCase()} is not implemented yet.`
    );
  };

  const handleExportSelectedCalls = async (
    callIds: string[],
    type: "pdf" | "excel"
  ) => {
    toast.info(
      `Exporting selected calls as ${type.toUpperCase()} is not implemented yet.`
    );
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            {/* Page Header */}
            <div className="page-header">
              <div className="row align-items-center">
                <div className="col-8">
                  <h4 className="page-title">
                    <i className="ti ti-phone-call text-primary me-2" />
                    Call Logs
                    <span className="count-title">{callLogs.length}</span>
                  </h4>
                </div>
                <div className="col-4 text-end">
                  <div className="head-icons">
                    <CollapseHeader />
                  </div>
                </div>
              </div>
            </div>
            {/* /Page Header */}
            <div className="card">
              <div className="card-header">
                {/* Search */}
                <div className="row align-items-center">
                  <div className="col-sm-4">
                    <div className="icon-form mb-3 mb-sm-0">
                      <span className="form-icon">
                        <i className="ti ti-search" />
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search Call Logs"
                        autoFocus
                        autoComplete="off"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="col-sm-8">
                    <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                      <div className="dropdown me-2">
                        <Link
                          to="#"
                          className="dropdown-toggle"
                          data-bs-toggle="dropdown"
                        >
                          <i className="ti ti-package-export me-2" />
                          Export
                        </Link>
                        <div className="dropdown-menu dropdown-menu-end">
                          <ul>
                            <li>
                              <Link
                                to="#"
                                className="dropdown-item"
                                onClick={() => handleExportAllCalls("pdf")}
                              >
                                <i className="ti ti-file-type-pdf text-danger me-1" />
                                Export as PDF
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="#"
                                className="dropdown-item"
                                onClick={() => handleExportAllCalls("excel")}
                              >
                                <i className="ti ti-file-type-xls text-green me-1" />
                                Export as Excel
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="#"
                                className="dropdown-item"
                                onClick={() =>
                                  handleExportSelectedCalls(
                                    selectedCalls,
                                    "pdf"
                                  )
                                }
                              >
                                <i className="ti ti-file-type-pdf text-danger me-1" />
                                Export Selected as PDF
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="#"
                                className="dropdown-item"
                                onClick={() =>
                                  handleExportSelectedCalls(
                                    selectedCalls,
                                    "excel"
                                  )
                                }
                              >
                                <i className="ti ti-file-type-xls text-green me-1" />
                                Export Selected as Excel
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="#"
                                className="dropdown-item"
                                onClick={() => setShowExportModal(true)}
                              >
                                <i className="ti ti-file-type-csv text-primary me-1" />
                                Customized Export
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* /Search */}
              </div>
              <div className="card-body">
                {/* Filter */}
                <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    <div className="dropdown me-2">
                      <Link
                        to="#"
                        className="dropdown-toggle"
                        data-bs-toggle="dropdown"
                      >
                        <i className="ti ti-sort-ascending-2 me-2" />
                        Sort
                      </Link>
                      <div className="dropdown-menu dropdown-menu-start">
                        <ul>
                          {[
                            {
                              label: "Ascending",
                              key: "start_time",
                              order: "asc",
                            },
                            {
                              label: "Descending",
                              key: "start_time",
                              order: "desc",
                            },
                            {
                              label: "Recently Added",
                              key: "start_time",
                              order: "desc",
                            },
                          ].map(({ label, key, order }) => {
                            const isActive =
                              sortKey === key && sortOrder === order;
                            return (
                              <li key={label}>
                                <Link
                                  to="#"
                                  className={`dropdown-item d-flex justify-content-between align-items-center ${
                                    isActive ? "fw-bold text-primary" : ""
                                  }`}
                                  onClick={() => {
                                    setSortKey(key);
                                    setSortOrder(order as "asc" | "desc");
                                  }}
                                >
                                  <span>
                                    <i className="ti ti-circle-chevron-right me-1" />
                                    {label}
                                  </span>
                                  {isActive && <i className="ti ti-check" />}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                    <div className="dropdown me-2">
                      <Link
                        to="#"
                        className="dropdown-toggle"
                        data-bs-toggle="dropdown"
                      >
                        <i className="ti ti-filter-check me-2" />
                        Call Status
                      </Link>
                      <div className="dropdown-menu dropdown-menu-start">
                        <ul>
                          <li>
                            <Link
                              to="#"
                              className={`dropdown-item d-flex justify-content-between align-items-center ${
                                activeStatusTab === null
                                  ? "fw-bold text-primary"
                                  : ""
                              }`}
                              onClick={(e) => {
                                e.preventDefault();
                                setActiveStatusTab(null);
                              }}
                            >
                              <span>
                                <i className="ti ti-circle-dot me-1" />
                                All
                              </span>
                              <span className="badge bg-light text-dark border">
                                {Object.values(callStatusCounts).reduce(
                                  (acc, count) => acc + count,
                                  0
                                )}
                              </span>
                              {activeStatusTab === null && (
                                <i className="ti ti-check ms-2 text-success" />
                              )}
                            </Link>
                          </li>
                          {[
                            { key: "Answered", label: "Answered" },
                            { key: "Missed", label: "Missed" },
                            { key: "Failed", label: "Failed" },
                          ].map(({ key, label }) => {
                            const isActive = activeStatusTab === key;
                            return (
                              <li key={key}>
                                <Link
                                  to="#"
                                  className={`dropdown-item d-flex justify-content-between align-items-center ${
                                    isActive ? "fw-bold text-primary" : ""
                                  }`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setActiveStatusTab((prev) =>
                                      prev === key ? null : key
                                    );
                                  }}
                                >
                                  <span>
                                    <i className="ti ti-circle-chevron-right me-1" />
                                    {label}
                                  </span>
                                  <span className="badge bg-light text-dark border">
                                    {callStatusCounts[key] || 0}
                                  </span>
                                  {isActive && (
                                    <i className="ti ti-check ms-2 text-success" />
                                  )}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                    <div className="icon-form">
                      <span className="form-icon">
                        <i className="ti ti-calendar" />
                      </span>
                      <DateRangePicker
                        initialSettings={initialSettings}
                        onCallback={(start, end) => {
                          setFilters((prev) => ({
                            ...prev,
                            startDate: start.toDate(),
                            endDate: end.toDate(),
                          }));
                        }}
                      >
                        <input
                          className="form-control date-range bookingrange"
                          type="text"
                        />
                      </DateRangePicker>
                    </div>
                  </div>
                  <div className="d-flex align-items-center flex-wrap row-gap-2">
                    <div className="dropdown me-2">
                      <Link
                        to="#"
                        className="btn bg-soft-purple text-purple"
                        data-bs-toggle="dropdown"
                        data-bs-auto-close="outside"
                      >
                        <i className="ti ti-columns-3 me-2" />
                        Manage Columns
                      </Link>
                      <div className="dropdown-menu dropdown-menu-md-end dropdown-md p-3">
                        <h4 className="mb-2 fw-semibold">
                          Manage Call Log Columns
                        </h4>
                        <p className="mb-3">
                          Please drag and drop your column to reorder your table
                          and enable see option as you want.
                        </p>
                        <div className="border-top pt-3">
                          {columnConfig.map((col) => (
                            <div
                              key={col.key}
                              className="d-flex align-items-center justify-content-between mb-3"
                            >
                              <p className="mb-0 d-flex align-items-center">
                                <i className="ti ti-grip-vertical me-2" />
                                {col.label}
                              </p>
                              <div className="status-toggle">
                                <input
                                  type="checkbox"
                                  id={`col-${col.key}`}
                                  className="check"
                                  checked={col.visible}
                                  onChange={() => handleColumnToggle(col.key)}
                                />
                                <label
                                  htmlFor={`col-${col.key}`}
                                  className="checktoggle"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="form-sorts dropdown me-2">
                      <Link
                        to="#"
                        data-bs-toggle="dropdown"
                        data-bs-auto-close="outside"
                      >
                        <i className="ti ti-filter-share" /> Filter
                      </Link>
                      <div className="filter-dropdown-menu dropdown-menu dropdown-menu-md-end p-3">
                        <div className="filter-set-view">
                          <div className="filter-set-head">
                            <h4>
                              <i className="ti ti-filter-share" /> Filter
                            </h4>
                          </div>
                          <div className="accordion" id="accordionCallFilter">
                            <div className="filter-set-content">
                              <div className="filter-set-content-head">
                                <Link
                                  to="#"
                                  data-bs-toggle="collapse"
                                  data-bs-target="#callLead"
                                >
                                  Lead ID
                                </Link>
                              </div>
                              <div
                                id="callLead"
                                className="accordion-collapse collapse show"
                              >
                                <div className="filter-content-list">
                                  <div className="mb-2 icon-form">
                                    <span className="form-icon">
                                      <i className="ti ti-search" />
                                    </span>
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Search Lead ID"
                                      value={filters.leadId}
                                      onChange={(e) =>
                                        setFilters((prev) => ({
                                          ...prev,
                                          leadId: e.target.value,
                                        }))
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="filter-set-content">
                              <div className="filter-set-content-head">
                                <Link
                                  to="#"
                                  className="collapsed"
                                  data-bs-toggle="collapse"
                                  data-bs-target="#callUser"
                                >
                                  Agent
                                </Link>
                              </div>
                              <div
                                id="callUser"
                                className="accordion-collapse collapse"
                              >
                                <div className="filter-content-list">
                                  <div className="mb-2 icon-form">
                                    <span className="form-icon">
                                      <i className="ti ti-search" />
                                    </span>
                                    <input
                                      type="text"
                                      className="form-control"
                                      placeholder="Search Agent ID"
                                      value={filters.userId}
                                      onChange={(e) =>
                                        setFilters((prev) => ({
                                          ...prev,
                                          userId: e.target.value,
                                        }))
                                      }
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="filter-set-content">
                              <div className="filter-set-content-head">
                                <Link
                                  to="#"
                                  className="collapsed"
                                  data-bs-toggle="collapse"
                                  data-bs-target="#callStatus"
                                >
                                  Status
                                </Link>
                              </div>
                              <div
                                id="callStatus"
                                className="accordion-collapse collapse"
                              >
                                <div className="filter-content-list">
                                  <ul>
                                    {["Answered", "Missed", "Failed"].map(
                                      (status) => (
                                        <li key={status}>
                                          <div className="filter-checks">
                                            <label className="checkboxs">
                                              <input
                                                type="checkbox"
                                                checked={filters.status.includes(
                                                  status
                                                )}
                                                onChange={() => {
                                                  const updated =
                                                    filters.status.includes(
                                                      status
                                                    )
                                                      ? filters.status.filter(
                                                          (s) => s !== status
                                                        )
                                                      : [
                                                          ...filters.status,
                                                          status,
                                                        ];
                                                  setFilters((prev) => ({
                                                    ...prev,
                                                    status: updated,
                                                  }));
                                                }}
                                              />
                                              <span className="checkmarks" />
                                              {status}
                                            </label>
                                          </div>
                                        </li>
                                      )
                                    )}
                                  </ul>
                                </div>
                              </div>
                            </div>
                            <div className="d-flex justify-content-between mt-3">
                              <button
                                type="button"
                                className="btn btn-light"
                                onClick={() =>
                                  setFilters({
                                    leadId: "",
                                    userId: "",
                                    status: [],
                                    startDate: null,
                                    endDate: null,
                                    username: "",
                                  })
                                }
                              >
                                Reset
                              </button>
                              <button type="button" className="btn btn-primary">
                                Apply
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* /Filter */}
                {/* Call Logs List */}
                <div className="table-responsive custom-table">
                  <Table
                    columns={columns}
                    dataSource={processedCalls}
                    rowKey="id"
                    loading={loading}
                    rowSelection={{
                      selectedRowKeys: selectedCalls,
                      onChange: (selectedRowKeys) =>
                        setSelectedCalls(selectedRowKeys.map(String)),
                    }}
                    pagination={{
                      current: currentPage,
                      pageSize: pageSize,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "20", "50"],
                      onChange: (page: number, size: number) => {
                        setCurrentPage(page);
                        setPageSize(size);
                      },
                    }}
                    scroll={{ x: "max-content" }}
                    components={{
                      header: {
                        cell: (props: any) => (
                          <th
                            {...props}
                            style={{
                              ...props.style,
                              position: "sticky",
                              top: 0,
                              zIndex: 2,
                              background: "#fff",
                              boxShadow: "inset 0 -1px 0 #dee2e6",
                            }}
                          />
                        ),
                      },
                    }}
                  />
                </div>
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <div className="datatable-length" />
                  </div>
                  <div className="col-md-6">
                    <div className="datatable-paginate" />
                  </div>
                </div>
                {/* /Call Logs List */}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Export Modal */}
      <Modal show={showExportModal} onHide={() => setShowExportModal(false)}>
        <div className="modal-header border-0 m-0 justify-content-end">
          <button
            className="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
            onClick={() => setShowExportModal(false)}
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="modal-body">
          <h4 className="mb-3">Customized Export</h4>
          <div className="mb-3">
            <label className="col-form-label">Export Type</label>
            <select
              className="form-select"
              value={exportType}
              onChange={(e) => setExportType(e.target.value as "pdf" | "excel")}
            >
              <option value="pdf">PDF</option>
              <option value="excel">Excel</option>
            </select>
          </div>
          <div className="mb-3">
            <label className="col-form-label">Select Columns</label>
            {exportColumnConfig.map((col) => (
              <div key={col.key} className="d-flex align-items-center mb-2">
                <input
                  type="checkbox"
                  id={`export-col-${col.key}`}
                  className="me-2"
                  checked={selectedExportColumns.includes(col.key)}
                  onChange={() => {
                    setSelectedExportColumns((prev) =>
                      prev.includes(col.key)
                        ? prev.filter((c) => c !== col.key)
                        : [...prev, col.key]
                    );
                  }}
                />
                <label htmlFor={`export-col-${col.key}`}>{col.label}</label>
              </div>
            ))}
          </div>
          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-light me-2"
              onClick={() => setShowExportModal(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => handleExportAllCalls(exportType)}
            >
              Export
            </button>
          </div>
        </div>
      </Modal>
      {/* Recording Modal */}
      {isRecordingModalOpen && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content rounded-4 overflow-hidden">
              <div
                className="px-4 py-2 d-flex justify-content-between align-items-center text-white"
                style={{
                  background: "linear-gradient(to right, rgb(182, 182, 182))",
                }}
              >
                <span className="fw-semibold">
                  {activeRecording &&
                    new Date(activeRecording.start_time).toLocaleString()}
                </span>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={closeRecordingModal}
                ></button>
              </div>
              <div className="modal-body text-center py-4 px-4">
                <p className="text-muted mb-3">
                  {activeRecording?.company_name || "Unknown"}
                </p>
                <h5 className="fw-bold mb-1">
                  {activeRecording?.company_phone || "Unknown"}
                </h5>
                <audio
                  controls
                  className="w-100 rounded-pill"
                  style={{
                    backgroundColor: "#f8f9fa",
                    padding: "0.25rem 1rem",
                  }}
                >
                  <source
                    src={activeRecording?.call_recording ?? ""}
                    type="audio/mpeg"
                  />
                  Your browser does not support the audio element.
                </audio>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* /Recording Modal */}
    </div>
  );
};

export default CallSettings;
